import React, { useState, useCallback } from 'react';
import { User, Lock, BookOpen, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

// InputField component defined OUTSIDE of the main component to prevent re-creation
const InputField = ({ 
  type, 
  name, 
  placeholder, 
  icon: Icon, 
  showToggle = false,
  showPassword,
  onToggleShow,
  value,
  onChange,
  validationError,
  disabled
}) => (
  <div className="relative group">
    <Icon className="absolute left-4 top-4 w-5 h-5 text-gray-400 transition-colors group-focus-within:text-primary z-10" />
    <input
      type={showToggle && showPassword ? 'text' : type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`w-full pl-12 ${showToggle ? 'pr-12' : 'pr-4'} py-4 border-2 rounded-xl transition-all duration-300 ${
        validationError 
          ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
          : 'border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary hover:border-gray-300 bg-white/90 backdrop-blur-sm'
      } disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 placeholder-gray-500`}
      disabled={disabled}
      autoComplete={type === 'password' ? 'current-password' : 'off'}
    />
    {showToggle && (
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        disabled={disabled}
        tabIndex={-1}
      >
        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    )}
    {validationError && (
      <div className="mt-2 text-sm text-red-600 flex items-center animate-slideIn">
        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
        <span>{validationError}</span>
      </div>
    )}
  </div>
);

const Login = () => {
  const { login, setPassword, isFirstLogin, loading, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Enhanced error message formatter
  const formatErrorMessage = (errorMessage) => {
    console.log('🔍 Formatting error message:', errorMessage);
    
    if (!errorMessage) return '';
    
    const message = errorMessage.toLowerCase();
    
    // Check for specific error types and return user-friendly messages
    if (message.includes('invalid credentials') || 
        message.includes('credenciales') ||
        message.includes('unauthorized')) {
      return 'Credenciales incorrectas. Verifica tu usuario y contraseña.';
    }
    
    if (message.includes('user not found') || 
        message.includes('usuario no encontrado')) {
      return 'El usuario no existe. Verifica que el nombre de usuario sea correcto.';
    }
    
    if (message.includes('password') && message.includes('incorrect')) {
      return 'La contraseña es incorrecta. Inténtalo de nuevo.';
    }
    
    if (message.includes('network') || message.includes('connection')) {
      return 'Error de conexión. Verifica tu conexión a internet e inténtalo de nuevo.';
    }
    
    if (message.includes('server') || message.includes('500')) {
      return 'Error del servidor. Por favor inténtalo más tarde.';
    }
    
    // Default fallback message
    return 'Error al iniciar sesión. Verifica tus credenciales e inténtalo de nuevo.';
  };

  // Fixed input change handler
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Clear errors when user starts typing
    if (error) {
      clearError();
    }
    
    if (loginError) {
      setLoginError('');
    }
  }, [validationErrors, error, clearError, loginError]);

  const validateLoginForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = 'El usuario es requerido';
    } else if (formData.username.length < 2) {
      errors.username = 'El usuario debe tener al menos 2 caracteres';
    }
    
    if (!formData.password.trim()) {
      errors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 3) {
      errors.password = 'La contraseña debe tener al menos 3 caracteres';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePasswordForm = () => {
    const errors = {};
    
    if (!formData.newPassword) {
      errors.newPassword = 'La nueva contraseña es requerida';
    } else if (formData.newPassword.length < 6) {
      errors.newPassword = 'La contraseña debe tener al menos 6 caracteres';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirma tu contraseña';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateLoginForm()) return;
    
    try {
      setIsSubmitting(true);
      setLoginError(''); // Clear previous login errors
      clearError(); // Clear auth context errors
      
      console.log('🔐 Attempting login for:', formData.username);
      
      await login(formData.username, formData.password);
      
      console.log('✅ Login successful');
      
      // Clear form after successful login
      setFormData({
        username: '',
        password: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (error) {
      console.error('❌ Login failed:', error);
      
      // Set user-friendly error message
      const friendlyMessage = formatErrorMessage(error.message);
      setLoginError(friendlyMessage);
      
      // Also log the original error for debugging
      console.error('Original error:', error.message);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) return;
    
    try {
      setIsSubmitting(true);
      setLoginError(''); // Clear any previous errors
      clearError();
      
      console.log('🔑 Setting password...');
      
      await setPassword(formData.newPassword);
      
      console.log('✅ Password set successfully');
      
    } catch (error) {
      console.error('❌ Password setup failed:', error);
      
      // Set user-friendly error message for password setup
      const friendlyMessage = error.message || 'Error al establecer la contraseña. Inténtalo de nuevo.';
      setLoginError(friendlyMessage);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine which error to show (prioritize loginError over global error)
  const displayError = loginError || error;

  if (isFirstLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary via-primary-dark to-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-primary/20 to-transparent"></div>
          <div className="absolute top-1/4 left-1/3 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-60"></div>
          <div className="absolute top-2/3 left-1/4 w-1 h-1 bg-blue-200 rounded-full animate-pulse opacity-40 delay-100"></div>
          <div className="absolute top-1/2 right-1/3 w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse opacity-50 delay-200"></div>
          <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-30 delay-300"></div>
          <div className="absolute top-3/4 left-2/3 w-1 h-1 bg-blue-200 rounded-full animate-pulse opacity-70 delay-500"></div>
        </div>
        
        <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10 border border-white/20">
          <div className="text-center mb-8">
            {/* Company Logo */}
            <div className="mb-6">
              <div className="relative mx-auto w-24 h-24 mb-4">
                <img 
                  src="/logo.jpg" 
                  alt="Puerto Madero Urubó" 
                  className="w-full h-full rounded-full object-cover shadow-lg ring-4 ring-primary/20"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="bg-primary w-full h-full rounded-full flex items-center justify-center shadow-lg ring-4 ring-primary/20 hidden"
                  style={{display: 'none'}}
                >
                  <BookOpen className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-primary mb-3">¡Bienvenido!</h1>
            <p className="text-gray-600 mb-2">Por favor establece tu contraseña para continuar</p>
            <p className="text-sm text-primary font-medium">Universidad de Asesores</p>
            <p className="text-xs text-gray-500">Puerto Madero Urubó</p>
          </div>

          {displayError && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-red-800 font-medium mb-1">Error al configurar contraseña</h4>
                  <p className="text-red-700 text-sm">{displayError}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSetPassword} className="space-y-6">
            <InputField
              type="password"
              name="newPassword"
              placeholder="Nueva contraseña (mín. 6 caracteres)"
              icon={Lock}
              showToggle={true}
              showPassword={showNewPassword}
              onToggleShow={() => setShowNewPassword(!showNewPassword)}
              value={formData.newPassword}
              onChange={handleInputChange}
              validationError={validationErrors.newPassword}
              disabled={isSubmitting}
            />

            <InputField
              type="password"
              name="confirmPassword"
              placeholder="Confirmar contraseña"
              icon={Lock}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              validationError={validationErrors.confirmPassword}
              disabled={isSubmitting}
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-white py-4 rounded-xl hover:bg-primary-dark transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl text-lg"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Configurando...
                </>
              ) : (
                'Establecer Contraseña'
              )}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-gray-500">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-blue-700">Una vez configurada tu contraseña, podrás acceder a todos los módulos de capacitación.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-primary-dark to-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-primary/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/3 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-2/3 left-1/4 w-1 h-1 bg-blue-200 rounded-full animate-pulse opacity-40 delay-100"></div>
        <div className="absolute top-1/2 right-1/3 w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse opacity-50 delay-200"></div>
        <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-30 delay-300"></div>
        <div className="absolute top-3/4 left-2/3 w-1 h-1 bg-blue-200 rounded-full animate-pulse opacity-70 delay-500"></div>
        <div className="absolute top-1/6 right-1/6 w-1.5 h-1.5 bg-blue-300 rounded-full animate-pulse opacity-40 delay-700"></div>
      </div>
      
      <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-5 w-full max-w-md relative z-10 border border-white/20">
        <div className="text-center mb-8">
          {/* Company Logo */}
          <div className="mb-6">
            <div className="relative mx-auto w-24 h-24 mb-4">
              <img 
                src="/logo.jpg" 
                alt="Puerto Madero Urubó" 
                className="w-full h-full rounded-full object-cover shadow-lg ring-4 ring-primary/20"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div 
                className="bg-primary w-full h-full rounded-full flex items-center justify-center shadow-lg ring-4 ring-primary/20 hidden"
                style={{display: 'none'}}
              >
                <BookOpen className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-primary mb-3">Universidad de Asesores</h1>
          <p className="text-gray-600 font-medium">Puerto Madero Urubó</p>
        </div>

        {displayError && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-red-800 font-medium mb-1">Error de autenticación</h4>
                <p className="text-red-700 text-sm">{displayError}</p>
                <div className="mt-3 text-xs text-red-600 space-y-1">
                  <p>• Verifica que tu usuario sea correcto</p>
                  <p>• Asegúrate de que la contraseña sea la correcta</p>
                  <p>• Si es tu primer acceso, no necesitas contraseña</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <InputField
            type="text"
            name="username"
            placeholder="Usuario (ej: cvargas)"
            icon={User}
            value={formData.username}
            onChange={handleInputChange}
            validationError={validationErrors.username}
            disabled={isSubmitting}
          />

          <InputField
            type="password"
            name="password"
            placeholder="Contraseña"
            icon={Lock}
            showToggle={true}
            showPassword={showPassword}
            onToggleShow={() => setShowPassword(!showPassword)}
            value={formData.password}
            onChange={handleInputChange}
            validationError={validationErrors.password}
            disabled={isSubmitting}
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary text-white py-4 rounded-xl hover:bg-primary-dark transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl text-lg"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Iniciando sesión...
              </>
            ) : (
              'Iniciar Sesión'
            )}
          </button>
        </form>

        <div className="mt-8 text-center space-y-4">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-sm text-gray-600 mb-2 font-medium">¿Necesitas ayuda con tu usuario?</p>
            <p className="text-sm text-gray-700">Tu usuario se forma con la <strong>primera letra de tu nombre</strong> + <strong>apellido completo</strong></p>
            <div className="mt-3 bg-white rounded-lg p-3 border border-blue-100">
              <p className="text-xs text-primary font-medium">
                Ejemplo: Cecilia Vargas → <code className="bg-primary/10 px-2 py-1 rounded font-mono text-primary">cvargas</code>
              </p>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-4">
            <p className="text-xs text-gray-400">
              © 2024 Puerto Madero Urubó. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;